import type { CSSProperties, ReactElement } from 'react';
import type {
    ControllerFieldState,
    ControllerProps,
    ControllerRenderProps,
    FieldPath,
    FieldValues,
    UseFormStateReturn
} from 'react-hook-form';
import { Typography } from '@ui';
import { cloneElement, useId } from 'react';
import { Controller } from 'react-hook-form';
import styles from './FormItem.module.css';

type RenderProps<T extends FieldValues, K extends FieldPath<T>> = {
    field: ControllerRenderProps<T, K> & {
        /**
         * required for htmlFor attribute in <label /> when using
         * ```jsx
         *   <FormItem label="something" />
         *   ```
         */
        id?: string;
    };
    fieldState: ControllerFieldState;
    formState: UseFormStateReturn<T>;
};

type Props<T extends FieldValues, K extends FieldPath<T>> = {
    flex?: CSSProperties['flex'];
    label?: string;
    render: (props: RenderProps<T, K>) => ReactElement;
} & Omit<ControllerProps<T, K>, 'render'>;

export const FormItem = <
    T extends FieldValues,
    K extends FieldPath<T>
>({ flex = '1 0', label, render: propRender, ...props }: Props<T, K>) => (
    <Controller
        render={(props) => {
            const error = props.fieldState.error?.message;
            const labelId = useId();
            return (
                <fieldset style={{ flex }} className={styles.fieldset}>
                    {label && <label htmlFor={labelId} className={styles.label}>{label}</label>}
                    {cloneElement(
                        propRender({ ...props, field: { ...props.field, ...(label ? { id: labelId } : {}) } }),
                        { error: Boolean(error) } as Record<string, string | number | boolean>
                    )}
                    {error && <Typography.Paragraph level={3} color="strokeError">{error}</Typography.Paragraph>}
                </fieldset>
            );
        }}
        {...props}
    />
);
